import LevelStateHelper from '../../Level/LevelStateHelper';
import { LevelTask } from '../../Level/LevelTask';
import Game from 'Game';
import { track } from 'Utility/Tracking/Track';
import {
    rewardTrackingMap,
    TActionType,
    TGameArea,
    TransactionType,
    TRewardAction,
} from 'Utility/Tracking/DataSet/Transaction';
import { trackResourceEvent } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { LevelComponent } from '../../Level/LevelComponent';
import { IGameState } from '../IGameState';
import { InventoryStateHelper } from '../../Player/Inventory/InventoryStateHelper';
import { IInLevelUnlockData } from './IInLevelUnlockData';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { IPlayerState, PlayerFTUX } from 'State/Player/IPlayerState';
import { Immutable } from 'Utility/Types';
import { PlayerTask } from 'State/Player/PlayerTasks';
import { getCurrentLevelNumber } from 'State/Player/PlayerLevelHelper';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import { PlayerStateHelper } from 'State/Player/PlayerStateHelper';
import { EventType } from 'Utility/Helpers/Events/EventType';
import { IEventStateData } from 'State/Events/IEventsState';
import { ArrayHelper } from 'Utility/ArrayHelper';

export namespace FTUXStateHelper {
    /**
     * Set the enabled components for the current level based on player
     * unlocks (suitcase, boosters, etc.) and level number.
     * @param state
     */
    export const setLevelComponents = (state: IGameState): IGameState => {
        const componentSettings: IInLevelUnlockData[] = Game.resource.get('inLevelUnlockSettings');

        const components: LevelComponent[] = componentSettings
            .filter(
                (component: IInLevelUnlockData) =>
                    state.levelState.levelNumber >= component.level && component.componentType
            )
            .map((unlockedComponent: IInLevelUnlockData) => unlockedComponent.componentType);

        return {
            ...state,
            levelState: {
                ...state.levelState,
                components: components,
                cardsState: {
                    ...state.levelState.cardsState,
                    streakEnabled: components.indexOf(LevelComponent.BONUS_STREAK) !== -1,
                },
            },
        };
    };

    export const setLevelTasks = (state: IGameState): IGameState => {
        const levelCompleted = (state.levelState.completeData?.starsGained ?? 0) > 0;
        const firstComplete = state.levelState.completeData && state.levelState.completeData.firstComplete;
        const unlockSettings: IInLevelUnlockData[] = Game.resource.get('inLevelUnlockSettings');
        const completedFTUXs: FTUXType[] = [];

        unlockSettings.forEach(unlock => {
            if (state.levelState.levelNumber === unlock.level) {
                if (unlock.onComplete) {
                    state = addLevelTask(state, unlock, firstComplete);
                } else {
                    state = addLevelTask(state, unlock, state.levelState.firstAttempt);
                }

                if (unlock.FTUXType && levelCompleted) {
                    completedFTUXs.push(unlock.FTUXType);
                }
            } else if (
                (unlock.task === LevelTask.FTUX_BOOSTER_TNT ||
                    unlock.task === LevelTask.FTUX_BOOSTER_SHUFFLE ||
                    unlock.task === LevelTask.FTUX_BOOSTER_BULLS_EYE) &&
                state.levelState.levelNumber >= unlock.level
            ) {
                state = addLevelTask(state, unlock, state.levelState.firstAttempt);
                if (
                    unlock.boosterMin &&
                    state.playerState.inventory[unlock.boosterMin.type] <= unlock.boosterMin.value
                ) {
                    state = {
                        ...state,
                        playerState: {
                            ...state.playerState,
                            inventory: {
                                ...state.playerState.inventory,
                                [unlock.boosterMin.type]: unlock.boosterMin.value,
                            },
                        },
                    };
                }
            }
        });

        if (completedFTUXs.length > 0) {
            state = {
                ...state,
                playerState: FTUXStateHelper.updateFTUXProgress(state.playerState, completedFTUXs),
            };
        }

        const sortedTasks = [...state.levelState.tasks].sort((a, b) =>
            a.level ? (b.level ? a.level - b.level : -1) : -1
        );

        return {
            ...state,
            levelState: {
                ...state.levelState,
                tasks: sortedTasks,
            },
            playerState: {
                ...state.playerState,
            },
        };
    };

    export const addLevelTask = (state: IGameState, taskData: IInLevelUnlockData, giveReward?: boolean): IGameState => {
        if (taskData.task) {
            state = LevelStateHelper.setStateTaskData(state, taskData);
        }

        if (taskData.reward && giveReward) {
            state = {
                ...state,
                playerState: InventoryStateHelper.giveReward(state.playerState, taskData.reward, {
                    queueTask: taskData.showRewardAnimation,
                }),
            };
            if (
                rewardTrackingMap.has(taskData.reward.type) &&
                rewardTrackingMap.get(taskData.reward.type).has(TRewardAction.RECEIVED)
            ) {
                track(rewardTrackingMap.get(taskData.reward.type).get(TRewardAction.RECEIVED), taskData.reward.value);
            }
            track('tGameArea', TGameArea.MAP);
            track('transactionType', TransactionType.REWARD);
            track('tActionType', TActionType.REWARD);
            track('tLevelContext', undefined);
            trackResourceEvent(state);
        }

        return state;
    };

    export const isFtuxCompleted = (playerState: Immutable<IPlayerState>, ftuxType: PlayerFTUX): boolean => {
        return playerState.FTUX.includes(ftuxType);
    };

    export const clearFTUXProgress = (state: IGameState, ...typesToClear: PlayerFTUX[]): IGameState => {
        const newFTUXProgress: PlayerFTUX[] =
            typesToClear.length > 0
                ? state.playerState.FTUX.filter((value: FTUXType) => !typesToClear.includes(value))
                : [];

        return {
            ...state,
            playerState: {
                ...state.playerState,
                FTUX: newFTUXProgress,
            },
        };
    };

    export const updateFTUXProgress = (playerState: IPlayerState, unlocks: PlayerFTUX[]): IPlayerState => {
        return {
            ...playerState,
            FTUX: [...playerState.FTUX, ...unlocks.filter(ftuxUnlock => !isFtuxCompleted(playerState, ftuxUnlock))],
        };
    };

    export function shouldQueueEventFTUX(state: IGameState): boolean {
        return getEventsWithUncompletedFTUX(state).length > 0;
    }

    function getEventsWithUncompletedFTUX(state: IGameState): IEventStateData[] {
        return EventStateHelper.getEvents(state, {
            isUnlocked: true,
            hasFTUX: true,
        }).filter(event => {
            return !FTUXStateHelper.isFtuxCompleted(state.playerState, event.type);
        });
    }

    export function queueEventFTUXIfNeeded(state: IGameState): IGameState {
        const eventsWithUncompletedFTUX = getEventsWithUncompletedFTUX(state);
        if (eventsWithUncompletedFTUX.length > 0) {
            const highestPrioEvent = ArrayHelper.sortByHighestPriority(eventsWithUncompletedFTUX)[0];
            return {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                    {
                        task: PlayerTask.EVENT_FTUX,
                        eventType: highestPrioEvent,
                    },
                ]),
            };
        }

        return state;
    }
}
